import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import UploadItemsHeader from "./AdminUploadComponents/UploadItemsHeader";
import SingleItemForm from "./AdminUploadComponents/SingleItemForm";
import BulkUploadSection from "./AdminUploadComponents/BulkUploadSection";
import ExtractCatalogueTab from "./Catalogue/ExtractCatalogueTab";
import useCatalogue from "../Services/Catalogue/useCatalogue";

const AdminUploadItems = ({ userID, onUploaded }) => {
  const {
    getAllInventoryVariant,
    getInventorySubCategories,
    getRackDetails,
    getAdminUomMaster,
    getAdminSizeMaster,
    uploadAdminItemImage,
    insertAdminCatalogItem,
  } = useCatalogue();

  const [variants, setVariants] = useState([]);
  const [racks, setRacks] = useState([]);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [mode, setMode] = useState("single");
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");

  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [subcategorySearch, setSubcategorySearch] = useState("");

  const [uomOptions, setUomOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [uomLoading, setUomLoading] = useState(false);
  const [sizeLoading, setSizeLoading] = useState(false);

  const [formData, setFormData] = useState({
    itemName: "",
    status: "Y",
    rating: "",
    description: "",
    tags: "",
    photo: "",
    vegIcon: "",
    category: "",
    subcategory: "",
    rack: "",
    variant: "",
    uom: "",
    size: "",
    price: "",
    discount: "",
    discountedPrice: "",
    barcode: "",
    expiryDate: "",
    homeDeliveryPrice: "",
    takeawayPrice: "",
    diningPrice: "",
    roomOrderPrice: "",
    carOrderPrice: "",
  });

  const getOptionValue = (item) =>
    item?.uom_code || item?.size_code || item?.code || item?.value || "";

  const getOptionLabel = (item) =>
    item?.uom_name ||
    item?.size_name ||
    item?.name ||
    item?.label ||
    item?.value ||
    "-";

  useEffect(() => {
    if (!userID) return;

    loadSubCategories();
    loadRacks();
    loadVariants();
    loadUomMaster();
  }, [userID]);
  // helper function to handle UOM change and fetch size options based on selected UOM
  const loadSubCategories = async () => {
    const result = await getInventorySubCategories({
      userid: userID,
      category: "",
    });

    if (result.success) {
      setSubCategories(result.data || []);
    } else {
      setSubCategories([]);
      toast.error(result.message);
    }
  };

  const loadRacks = async () => {
    const result = await getRackDetails({ userid: userID });

    if (result.success) {
      setRacks(result.data || []);
    } else {
      setRacks([]);
      toast.error(result.message);
    }
  };

  const loadVariants = async () => {
    const result = await getAllInventoryVariant({ userid: userID });

    if (result.success) {
      setVariants(result.data || []);
    } else {
      setVariants([]);
      toast.error(result.message);
    }
  };

  const loadUomMaster = async () => {
    try {
      setUomLoading(true);

      const result = await getAdminUomMaster({ sizeType: "0" });

      if (result.success) {
        setUomOptions(result.data || []);
      } else {
        setUomOptions([]);
        toast.error(result.message);
      }
    } finally {
      setUomLoading(false);
    }
  };

  // helper function to handle UOM change and fetch size options based on selected UOM
  const handleUomChange = async (e) => {
    const selectedUom = e.target.value;

    setFormData((prev) => ({
      ...prev,
      uom: selectedUom,
      size: "",
    }));

    setSizeOptions([]);

    if (!selectedUom) return;

    try {
      setSizeLoading(true);

      const result = await getAdminSizeMaster({ sizeType: selectedUom });

      if (result.success) {
        setSizeOptions(result.data || []);
      } else {
        setSizeOptions([]);
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Size load error:", error);
      toast.error("Failed to fetch size data");
    } finally {
      setSizeLoading(false);
    }
  };

  useEffect(() => {
    if (subCategories?.length) {
      const unique = [
        ...new Set(
          subCategories
            .map((s) => s.category?.trim())
            .filter((c) => c && c !== ""),
        ),
      ];
      setCategories(unique);
    }
  }, [subCategories]);

  const sanitizeText = (value) => {
    return clean(value)
      .replace(/[^a-zA-Z0-9\s]/g, "") // remove all special characters
      .replace(/\s+/g, " ") // remove extra spaces
      .trim();
  };

  const sanitizeAlphaNumeric = (value) => {
    return clean(value)
      .replace(/[^a-zA-Z0-9]/g, "") // remove spaces and special characters
      .trim();
  };

  const sanitizePrice = (value) => {
    const raw = clean(value);

    if (!raw) return "";

    // remove currency symbols, commas, spaces, and all non-number characters except dot
    return raw.replace(/[^0-9.]/g, "");
  };

  const sanitizeDate = (value) => {
    const raw = clean(value);

    if (!raw) return "";

    // allow only numbers and slash
    return raw.replace(/-/g, "/").replace(/[^0-9/]/g, "");
  };

  const validateBulkItems = (items) => {
    const errors = [];

    items.forEach((item, index) => {
      const rowNumber = index + 2;

      if (!item.itemName) {
        errors.push(`Row ${rowNumber}: itemName is required`);
      }

      if (!item.price) {
        errors.push(`Row ${rowNumber}: price is required`);
      }

      if (item.price && isNaN(Number(item.price))) {
        errors.push(
          `Row ${rowNumber}: price must be a number. Found "${item.price}"`,
        );
      }

      if (item.discountPrice && isNaN(Number(item.discountPrice))) {
        errors.push(
          `Row ${rowNumber}: discountPrice must be a number. Found "${item.discountPrice}"`,
        );
      }

      if (item.iconDish && !["Y", "N"].includes(item.iconDish.toUpperCase())) {
        errors.push(`Row ${rowNumber}: iconDish should be Y or N`);
      }
    });

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price" || name === "discount") {
      const updated = { ...formData, [name]: value };
      const price = parseFloat(updated.price || 0);
      const discount = parseFloat(updated.discount || 0);

      updated.discountedPrice = price > 0 ? Math.max(price - discount, 0) : "";

      setFormData(updated);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        const base64 = reader.result;

        const result = await uploadAdminItemImage({
          userid: userID,
          item_name: formData.itemName || `item_${Date.now()}`,
          image_data: base64,
        });

        const res = result.data;

        //  FIXED CONDITION
        if (res?.status === "0" && res?.image_url) {
          setFormData((prev) => ({
            ...prev,
            photo: res.image_url,
          }));

          toast.success("Image uploaded ");
        } else {
          toast.error("Image upload failed ");
        }
      } catch (err) {
        console.error(err);
        toast.error("Upload error ");
      }
    };

    reader.readAsDataURL(file);
  };
const clean = (val) => {
  if (val === null || val === undefined) return "";

  return String(val)
    .replace(/^\uFEFF/, "") // remove BOM from Excel CSV
    .replace(/\0/g, "") // remove null chars
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  const parseCSV = (text) => {
    const rows = [];
    let row = [];
    let cell = "";
    let inQuotes = false;

    const value = String(text || "").replace(/^\uFEFF/, "");

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      const nextChar = value[i + 1];

      if (char === '"') {
        // Escaped quote inside quoted value: "" => "
        if (inQuotes && nextChar === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      // Comma outside quotes means next column
      if (char === "," && !inQuotes) {
        row.push(clean(cell));
        cell = "";
        continue;
      }

      // New line outside quotes means next row
      if ((char === "\n" || char === "\r") && !inQuotes) {
        // handle Windows CRLF
        if (char === "\r" && nextChar === "\n") {
          i++;
        }

        row.push(clean(cell));

        const hasAnyValue = row.some((v) => clean(v) !== "");
        if (hasAnyValue) rows.push(row);

        row = [];
        cell = "";
        continue;
      }

      cell += char;
    }

    // last cell / row
    row.push(clean(cell));
    const hasAnyValue = row.some((v) => clean(v) !== "");
    if (hasAnyValue) rows.push(row);

    return rows;
  };

  const normalizeHeader = (header) => {
    return clean(header).toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
  };

  const getValue = (row, keys, fallback = "") => {
    for (const key of keys) {
      const normalizedKey = normalizeHeader(key);
      if (row[normalizedKey] !== undefined && row[normalizedKey] !== null) {
        return clean(row[normalizedKey]);
      }
    }
    return fallback;
  };

  const normalizePrice = (value) => {
    return sanitizePrice(value);
  };

  const normalizeExpiryDate = (value) => {
    return sanitizeDate(value);
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const text = String(evt.target?.result || "");

        const rows = parseCSV(text);

        if (!rows.length) {
          setCsvErrors([]);
          setBulkItems([]);
          toast.error("CSV file is empty");
          return;
        }

        const headers = rows[0].map(normalizeHeader);
        const dataRows = rows.slice(1);

        const items = dataRows
          .map((cols) => {
            const row = {};

            headers.forEach((key, i) => {
              row[key] = clean(cols[i] || "");
            });

            const rawItemName = getValue(row, ["itemName", "item name"]);

            const price = normalizePrice(getValue(row, ["price"]));
            const discountPrice = normalizePrice(
              getValue(row, [
                "discountPrice",
                "discount_price",
                "discount price",
              ]),
            );
            let discount = "";
            if (price && discountPrice) {
              const p = parseFloat(price) || 0;
              const dp = parseFloat(discountPrice) || 0;

              if (p > dp) {
                discount = (p - dp).toFixed(2);
              } else {
                discount = "0";
              }
            }

            return {
              originalItemName: rawItemName,

              itemName: sanitizeText(rawItemName),

              itemDescription: sanitizeText(
                getValue(row, [
                  "itemDescription",
                  "item description",
                  "description",
                ]),
              ),

              // Do not sanitize URL with normal text sanitizer, otherwise https:// will break
              photo: clean(
                getValue(row, ["photo", "image", "imageUrl", "image url"]),
              ),

              category: sanitizeText(
                getValue(row, ["category", "categoryName", "category name"]),
              ),

              price: sanitizePrice(price),
              discount: sanitizePrice(discount),
              discountPrice: sanitizePrice(discountPrice),

              variantName: sanitizeText(
                getValue(row, ["variantName", "variant name", "variant"]),
              ),

              iconDish: sanitizeAlphaNumeric(
                getValue(row, ["iconDish", "icon dish", "dish", "veg"]),
              ).toUpperCase(),

              rackNumber: sanitizeAlphaNumeric(
                getValue(row, ["rackNumber", "rack number", "rack"]),
              ),
              expiryDate: normalizeExpiryDate(
                getValue(row, ["expiryDate", "expiry date"]),
              ),

              home_delivery_price: normalizePrice(
                getValue(row, [
                  "home_delivery_price",
                  "home delivery price",
                  "homeDeliveryPrice",
                ]),
              ),
              takeaway_price: normalizePrice(
                getValue(row, [
                  "takeaway_price",
                  "takeaway price",
                  "takeawayPrice",
                ]),
              ),
              dining_price: normalizePrice(
                getValue(row, ["dining_price", "dining price", "diningPrice"]),
              ),
              room_order_price: normalizePrice(
                getValue(row, [
                  "room_order_price",
                  "room order price",
                  "roomOrderPrice",
                ]),
              ),
              car_order_price: normalizePrice(
                getValue(row, [
                  "car_order_price",
                  "car order price",
                  "carOrderPrice",
                ]),
              ),

              status: "Y",
              ratings: "0",
              customizations: "[]",
            };
          })
          .filter((item) => {
            return (
              item.itemName ||
              item.itemDescription ||
              item.photo ||
              item.category ||
              item.price
            );
          });

        if (!items.length) {
          setCsvErrors([]);
          setBulkItems([]);
          toast.error("No valid items found in CSV");
          return;
        }

        const validationErrors = validateBulkItems(items);

        setCsvErrors(validationErrors);
        setBulkItems(items);

        console.log("Parsed CSV Items:", items);
        console.table(items);

        if (validationErrors.length > 0) {
          toast.warning(
            `${validationErrors.length} CSV issues found. Check below.`,
          );
        } else {
          toast.success(`CSV is valid. Parsed ${items.length} items safely.`);
        }
      } catch (error) {
        console.error("CSV parse error:", error);
        setCsvErrors([
          "CSV file could not be parsed. Please check the format.",
        ]);
        setBulkItems([]);
        toast.error("CSV file could not be parsed. Please check the format.");
      } finally {
        e.target.value = "";
      }
    };

    reader.onerror = () => {
      setCsvErrors(["Unable to read file"]);
      toast.error("Unable to read file");
    };

    reader.readAsText(file, "UTF-8");
  };

  const csvEscape = (value) => {
    const str = clean(value);

    // In CSV, double quote must become two double quotes
    const escaped = str.replace(/"/g, '""');

    // Always wrap in quotes so comma, slash, brackets, symbols, etc. stay safe
    return `"${escaped}"`;
  };

  const handleDownloadSample = () => {
    const headers = [
      "itemName",
      "itemDescription",
      "photo",
      "category",
      "price",
      "discountPrice",
      "variantName",
      "iconDish",
      "rackNumber",
      "expiryDate",
      "home_delivery_price",
      "takeaway_price",
      "dining_price",
      "room_order_price",
      "car_order_price",
    ];

    const sampleRow = [
      "Margherita Pizza",
      "Classic cheese pizza",
      "https://example.com/image.jpg",
      "Food",
      "199",
      "179",
      "Regular",
      "Y",
      "1",
      "2025/12/31",
      "189",
      "179",
      "199",
      "209",
      "219",
    ];

    const csv =
      "\uFEFF" +
      headers.map(csvEscape).join(",") +
      "\n" +
      sampleRow.map(csvEscape).join(",");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "catalogue_sample.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleDownloadBulkExcel = async () => {
    try {
      const result = await getAdminSizeMaster({ sizeType: "0" });
      const data = result?.data || [];

      if (!result?.success || data.length === 0) {
        toast.error(
          result?.message || "No size master data available to download",
        );
        return;
      }

      exportAnyJsonToExcel({
        data,
        filePrefix: "Size_Master",
        sheetName: "Size Master",
      });

      toast.success(`${data.length} size records downloaded successfully`);
    } catch (error) {
      console.error("Download size master Excel error:", error);
      toast.error("Failed to download size master Excel");
    }
  };
  const resetForm = () => {
    setFormData({
      itemName: "",
      status: "Y",
      rating: "",
      description: "",
      tags: "",
      photo: "",
      vegIcon: "",
      category: "",
      subcategory: "",
      rack: "",
      variant: "",
      uom: "",
      size: "",
      price: "",
      discount: "",
      discountedPrice: "",
      barcode: "",
      expiryDate: "",
      homeDeliveryPrice: "",
      takeawayPrice: "",
      diningPrice: "",
      roomOrderPrice: "",
      carOrderPrice: "",
    });

    setSizeOptions([]);
    setCategorySearch("");
    setSubcategorySearch("");
  };
  const handleSingleSave = async () => {
    try {
      const selectedVariant = variants.find(
        (v) => String(v.id || v.variant_id) === String(formData.variant),
      );

      const payload = {
        userid: userID,

        items: [
          {
            itemName: clean(formData.itemName),
            itemId: "",
            status: formData.status,

            itemDescription: clean(formData.description),
            dish: clean(formData.tags),

            category: clean(formData.categoryInput || formData.category),
            catId: "",

            price: clean(formData.price),
            discount: clean(formData.discount || "0"),
            discountPrice: clean(formData.discountedPrice),

            home_delivery_price: clean(formData.homeDeliveryPrice),
            takeaway_price: clean(formData.takeawayPrice),
            dining_price: clean(formData.diningPrice),
            room_order_price: clean(formData.roomOrderPrice),
            car_order_price: clean(formData.carOrderPrice),

            expiryDate: clean(formData.expiryDate?.replace(/-/g, "/")),

            rackNumber: clean(formData.rack),

            //  dynamic variant
            variantId: clean(formData.variant),
            variantName: clean(selectedVariant?.variant_title),

            photo: clean(formData.photo),
            iconDish: clean(formData.vegIcon),

            barcodeNumber: clean(formData.barcode),

            ratings: "0",
            customizations: "[]",

            categoryDescription: "",
            itemNameTrans: "",
            itemDescriptionTrans: "",
            position: "",
            sizeTrans: "",
            subCategory: "",
          },
        ],
      };

      const result = await insertAdminCatalogItem({
        userid: userID,
        items: payload.items,
      });

      if (result.success) {
        toast.success(result.message);
        onUploaded?.();
        resetForm();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSafeBulkSubmit = async () => {
    if (csvErrors.length > 0) {
      toast.error("Please fix CSV validation issues before upload.");
      return;
    }

    if (!bulkItems.length) {
      toast.error("Please select and validate CSV file first.");
      return;
    }

    try {
      setBulkLoading(true);

      const result = await insertAdminCatalogItem({
        userid: userID,
        items: bulkItems.map((item) => ({
          itemName: clean(item.itemName),
          itemId: "",
          status: "Y",

          itemDescription: clean(item.itemDescription),

          category: clean(item.category),
          catId: "",

          price: clean(item.price),
          discount: clean(item.discount || "0"),
          discountPrice: clean(item.discountPrice),

          home_delivery_price: clean(item.home_delivery_price),
          takeaway_price: clean(item.takeaway_price),
          dining_price: clean(item.dining_price),
          room_order_price: clean(item.room_order_price),
          car_order_price: clean(item.car_order_price),

          expiryDate: clean(item.expiryDate),
          rackNumber: clean(item.rackNumber),

          variantName: clean(item.variantName),
          variantId: "",

          photo: clean(item.photo),
          iconDish: clean(item.iconDish),

          ratings: "0",
          barcodeNumber: "",
          customizations: "[]",

          categoryDescription: "",
          dish: "",
          itemNameTrans: "",
          itemDescriptionTrans: "",
          position: "",
          sizeTrans: "",
          subCategory: "",
        })),
      });

      if (result.success) {
        toast.success(result.message || "Bulk upload success");
        setBulkItems([]);
        setCsvErrors([]);
        onUploaded?.();
      } else {
        toast.error(result.message);
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const flattenObject = (obj, parentKey = "", res = {}) => {
    for (const key in obj) {
      const propName = parentKey ? `${parentKey}_${key}` : key;

      if (Array.isArray(obj[key])) {
        obj[key].forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            flattenObject(item, `${propName}_${index}`, res);
          } else {
            res[`${propName}_${index}`] = item;
          }
        });
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }

    return res;
  };

  const exportAnyJsonToExcel = ({
    data = [],
    filePrefix = "Export",
    sheetName = "Sheet1",
  }) => {
    if (!Array.isArray(data) || data.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const flatData = data.map((item) => flattenObject(item));

    const worksheet = XLSX.utils.json_to_sheet(flatData, {
      skipHeader: false,
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const dateStr = new Date().toLocaleString("en-IN").replace(/[\/,: ]/g, "_");

    const fileName = `${filePrefix}_${dateStr}.xlsx`;

    saveAs(blob, fileName);
  };

  const filteredCategories = categories.filter((c) =>
    c.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  const filteredSubcategories = [
    ...new Map(
      subCategories
        ?.filter(
          (s) =>
            s.subCategory &&
            s.subCategory.trim() !== "" &&
            (!formData.category || s.category === formData.category) &&
            s.subCategory
              .toLowerCase()
              .includes(subcategorySearch.toLowerCase()),
        )
        .map((item) => [item.subCategory.trim(), item]),
    ).values(),
  ];

  return (
    <>
      <div className=" h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <UploadItemsHeader mode={mode} setMode={setMode} />

        {/* BODY */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="mt-2 space-y-3">
            {mode === "single" && (
              <SingleItemForm
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleImageChange={handleImageChange}
                handleUomChange={handleUomChange}
                handleSingleSave={handleSingleSave}
                showCategoryDropdown={showCategoryDropdown}
                setShowCategoryDropdown={setShowCategoryDropdown}
                setCategorySearch={setCategorySearch}
                filteredCategories={filteredCategories}
                showSubcategoryDropdown={showSubcategoryDropdown}
                setShowSubcategoryDropdown={setShowSubcategoryDropdown}
                setSubcategorySearch={setSubcategorySearch}
                filteredSubcategories={filteredSubcategories}
                uomOptions={uomOptions}
                uomLoading={uomLoading}
                sizeOptions={sizeOptions}
                sizeLoading={sizeLoading}
                getOptionValue={getOptionValue}
                getOptionLabel={getOptionLabel}
                racks={racks}
              />
            )}

            {/* BULK MODE */}
            {mode === "bulk" && (
              <BulkUploadSection
                handleDownloadSample={handleDownloadSample}
                handleDownloadBulkExcel={handleDownloadBulkExcel}
                handleBulkFileChange={handleBulkFileChange}
                csvErrors={csvErrors}
                bulkItems={bulkItems}
                bulkLoading={bulkLoading}
                handleSafeBulkSubmit={handleSafeBulkSubmit}
              />
            )}

            {/* Extract MODE */}
            {mode === "extract" && (
              <ExtractCatalogueTab userID={userID} onUploaded={onUploaded} />
            )}
          </div>
        </div>

        {/*  COMMON STYLES */}
        <style>{`
  .input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
  }
  .input:disabled {
    background-color: #e5e7eb;
    cursor: not-allowed;
  }
  .label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
    display: block;
  }
`}</style>
      </div>
    </>
  );
};

export default AdminUploadItems;
